import { useState, useEffect, useRef } from "react";
import { askClaude } from "../utils/api";
import { buildInterviewSystem } from "../utils/prompts";

export default function InterviewPanel({ role, company, topic, topicColor, onClose, apiKey = "" }) {
  const [messages, setMessages] = useState([]);
  const [apiMsgs, setApiMsgs]   = useState([]);
  const [input, setInput]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [scores, setScores]     = useState([]);
  const chatRef  = useRef(null);
  const sysPrompt = buildInterviewSystem(role, company, topic);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, loading]);

  const startInterview = async () => {
    setLoading(true);
    try {
      const initMsg = [{ role: "user", content: "I'm ready. Please start the interview." }];
      const resp = await askClaude("I'm ready. Please start the interview.", sysPrompt, apiKey);
      setApiMsgs([...initMsg, { role: "assistant", content: resp }]);
      setMessages([{ role: "ai", text: resp, type: "q" }]);
    } catch (e) {
      setMessages([{ role: "ai", text: `Error: ${e.message}. Check your API configuration.`, type: "q" }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { startInterview(); }, []);

  const parseEval = (text) => {
    const lines = text.split("\n");
    const get   = (key) => { const l = lines.find(l => l.startsWith(key + ":")); return l ? l.replace(key + ":", "").trim() : ""; };
    const score = parseInt(get("SCORE")) || 0;
    if (!score) return null;
    return { score, strengths: get("STRENGTHS"), gaps: get("GAPS"), expert: get("EXPERT_ANSWER"), next: get("NEXT_QUESTION"), hasEval: true };
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const txt = input.trim(); setInput("");
    setMessages(m => [...m, { role: "user", text: txt, type: "a" }]);
    setLoading(true);
    const newApi = [...apiMsgs, { role: "user", content: txt }];
    try {
      const resp = await askClaude(txt, sysPrompt, apiKey);
      setApiMsgs([...newApi, { role: "assistant", content: resp }]);
      const ev = parseEval(resp);
      if (ev) {
        setScores(s => [...s, ev.score]);
        setMessages(m => [...m, { role: "ai", text: resp, type: "eval", ev }]);
      } else {
        setMessages(m => [...m, { role: "ai", text: resp, type: "q" }]);
      }
    } catch (e) {
      setMessages(m => [...m, { role: "ai", text: "Error getting response. Please try again.", type: "q" }]);
    } finally {
      setLoading(false);
    }
  };

  const avg      = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
  const avgColor = avg ? (avg >= 8 ? "#34D399" : avg >= 6 ? "#FB923C" : "#F87171") : topicColor;

  return (
    <div style={{ position:"fixed", inset:0, background:"#000000dd", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:"#06070f", border:`1px solid ${topicColor}33`, borderRadius:20, width:"100%", maxWidth:700, height:"85vh", display:"flex", flexDirection:"column" }}>
        {/* Header */}
        <div style={{ padding:"14px 18px", borderBottom:`1px solid ${topicColor}22`, display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:800, fontSize:14, color:"#e2e8f0" }}>🎤 Mock Interview — {role} @ {company}</div>
            <div style={{ fontSize:11, color:"#475569", marginTop:2 }}>Topic: {topic} • {scores.length} questions answered</div>
          </div>
          {avg && <div style={{ fontSize:16, fontWeight:900, color:avgColor }}>Avg: {avg}/10</div>}
          <button onClick={onClose} style={{ background:"#1e2030", border:"none", borderRadius:6, width:28, height:28, color:"#94a3b8", cursor:"pointer", fontSize:15 }}>×</button>
        </div>

        {/* Chat */}
        <div ref={chatRef} style={{ flex:1, overflow:"auto", padding:"16px 18px", display:"flex", flexDirection:"column", gap:14 }}>
          {messages.map((msg, i) => (
            <div key={i} style={{ display:"flex", gap:10, flexDirection: msg.role === "user" ? "row-reverse" : "row", alignItems:"flex-start", maxWidth:"90%", marginLeft: msg.role === "user" ? "auto" : 0 }}>
              <div style={{ width:30, height:30, borderRadius:8, background: msg.role === "ai" ? `${topicColor}22` : "#1e2030", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>
                {msg.role === "ai" ? "🤖" : "👤"}
              </div>
              <div style={{ flex:1 }}>
                {msg.type === "eval" && msg.ev ? (
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    <div style={{ background:"#0a1020", border:`1px solid ${topicColor}22`, borderRadius:12, padding:"14px 16px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                        <div style={{ fontSize:20, fontWeight:900, color: msg.ev.score >= 8 ? "#34D399" : msg.ev.score >= 6 ? "#FB923C" : "#F87171" }}>{msg.ev.score}/10</div>
                        <div style={{ fontSize:13, fontWeight:700, color: msg.ev.score >= 8 ? "#34D399" : msg.ev.score >= 6 ? "#FB923C" : "#F87171" }}>
                          {msg.ev.score >= 8 ? "Excellent!" : msg.ev.score >= 6 ? "Good answer" : "Needs improvement"}
                        </div>
                      </div>
                      {msg.ev.strengths && <div style={{ marginBottom:8 }}><div style={{ fontSize:10, color:"#34D399", marginBottom:4, letterSpacing:1 }}>✓ STRENGTHS</div><div style={{ fontSize:12, color:"#94a3b8" }}>{msg.ev.strengths}</div></div>}
                      {msg.ev.gaps      && <div style={{ marginBottom:8 }}><div style={{ fontSize:10, color:"#FB923C", marginBottom:4, letterSpacing:1 }}>⚠ GAPS</div><div style={{ fontSize:12, color:"#94a3b8" }}>{msg.ev.gaps}</div></div>}
                      {msg.ev.expert    && <div style={{ marginBottom:10 }}><div style={{ fontSize:10, color:"#38BDF8", marginBottom:4, letterSpacing:1 }}>💡 EXPERT ANSWER</div><div style={{ fontSize:12, color:"#64748b", background:"#060710", borderRadius:6, padding:10 }}>{msg.ev.expert}</div></div>}
                      {msg.ev.next      && <div style={{ background:`${topicColor}12`, border:`1px solid ${topicColor}22`, borderRadius:8, padding:"10px 12px" }}><div style={{ fontSize:10, color:topicColor, marginBottom:5 }}>NEXT QUESTION</div><div style={{ fontSize:14, color:"#e2e8f0" }}>{msg.ev.next}</div></div>}
                    </div>
                  </div>
                ) : (
                  <div style={{ background: msg.role === "user" ? "#1e2030" : "#0d0e1a", border:`1px solid ${msg.role === "user" ? "#2a2d40" : topicColor + "22"}`, borderRadius:10, padding:"11px 14px", fontSize:13, color: msg.role === "user" ? "#e2e8f0" : "#94a3b8", lineHeight:1.7 }}>
                    {msg.text}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display:"flex", gap:8, alignItems:"center", color:"#475569", fontSize:13 }}>
              <div style={{ width:30, height:30, borderRadius:8, background:`${topicColor}22`, display:"flex", alignItems:"center", justifyContent:"center" }}>🤖</div>
              {[0, 1, 2].map(i => <div key={i} style={{ width:6, height:6, borderRadius:99, background:topicColor, opacity:0.7 }} />)}
            </div>
          )}
        </div>

        {/* Input */}
        <div style={{ padding:"12px 16px", borderTop:`1px solid ${topicColor}22` }}>
          <div style={{ display:"flex", gap:8 }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type your answer... (Enter to send, Shift+Enter for new line)"
              disabled={loading}
              rows={2}
              style={{ flex:1, background:"#0d0e1a", border:`1px solid ${topicColor}33`, borderRadius:10, padding:"10px 13px", color:"#e2e8f0", fontFamily:"inherit", fontSize:13, resize:"none", outline:"none", opacity: loading ? 0.5 : 1 }}
            />
            <button onClick={send} disabled={!input.trim() || loading} style={{ background: input.trim() && !loading ? topicColor : "#1e2030", border:"none", borderRadius:10, padding:"0 18px", color: input.trim() && !loading ? "#000" : "#475569", cursor: input.trim() && !loading ? "pointer" : "not-allowed", fontSize:18, fontWeight:700, transition:"all 0.15s" }}>→</button>
          </div>
        </div>
      </div>
    </div>
  );
}
